import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UncontrolledTooltip } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import documentReducer from './documentReducer';
import { LinkButton, SpinnerArea, Table, TablePagination, Modal } from '../../../shared/components';
import operationService from '../../../services/operationService';
import useServiceReducer from '../../../shared/hooks/useServiceReducer';
import { OperationDocument, DocumentFilter } from '../../../services/types/Operation';
import { Cell } from '../../../shared/components/Table';
import { dateStr, isDraft, tooltipPreventOverflowModifiers } from '../../../shared/misc/utils';
import OperationContext from './context';
import Context from '../../App/MainProvider/context';
import FileUploadButton from '../../../shared/components/FileUploadButton';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { getFlowTypeDisplayName } from '../../../shared/misc/flowTypeNames';

const Documents: React.FC = () => {
  const { t } = useTranslation();
  const { operation } = useContext(OperationContext);
  const { service, inSuperUserMode } = useContext(Context);

  const [docSelected, setDocSelected] = useState<OperationDocument | null>(null);
  const [conflict, setConflict] = useState(false);
  const [formData, setFormData] = useState<FormData | null>(null);
  const [errorUpload, setErrorUpload] = useState<string | null>(null);

  const canEdit = (operation.isMember || inSuperUserMode()) && !isDraft(operation);

  const initFilter: DocumentFilter = {
    operationId: operation.id,
    pageNumber: 1,
    sortField: 'documentName',
    sortDescending: false,
  };

  const [data, loading, filter, dispatch, loader, reload] = useServiceReducer(
    operationService.getDocuments,
    documentReducer,
    initFilter
  );

  const pageSize = 8;
  const changePage = (page: number) => dispatch({ type: 'PAGE', value: page });
  const changeSort = (column: string, descending: boolean) =>
    dispatch({ type: 'ORDER', column, descending });

  const upload = (fData: FormData) => {
    fData.append('parentId', operation.id.toString());
    loader(true);
    setErrorUpload(null);

    const callback = (result: boolean) => {
      if (result) {
        reload();
        return;
      }
      setFormData(fData);
      setConflict(true); // name conflict
    };

    service(() => operationService.uploadDocument(fData), { callback, loader });
  };

  const uploadFileError = (textError: string) => {
    let error = textError;
    if (error.toLocaleLowerCase() === 'oversize') {
      error = t('uploadMaxSizeError30');
    }
    setErrorUpload(error);
  };

  const conflictUpload = (replace: boolean) => {
    if (!formData) {
      return;
    }
    formData.append('replace', replace.toString());
    loader(true);
    service(() => operationService.uploadDocument(formData), { callback: () => reload() });
  };

  const download = (docId: number) => {
    service(() => operationService.downloadDocument(docId), { loader });
  };

  const downloadEzshare = (ezShareCode: string) => {
    window.open(`https://idbdocs.iadb.org/wsdocs/getdocument.aspx?docnum=${ezShareCode}`, '_blank');
  };
  const showDeleteModal = (obj: OperationDocument) => setDocSelected(obj);
  const closeModal = () => setDocSelected(null);

  const deleteDoc = () => {
    if (!docSelected) {
      return;
    }
    loader(true);

    const callback = () => {
      reload();
      setDocSelected(null);
    };

    service(() => operationService.deleteDocument(docSelected.id), { callback, loader });
  };

  const renderDocumentName = (obj: OperationDocument) => {
    const text = `${getFlowTypeDisplayName(obj.flowType)} ${obj.documentName}`;

    if (obj.source !== 'Legacy_Convergence') {
      return <span>{text}</span>;
    }

    const tooltipId = `doc-legacy-${obj.id}`;

    return (
      <>
        <span>{text}</span>

        <span id={tooltipId} className="ml-2 toolkit-tooltip">
          <FontAwesomeIcon icon="exclamation-circle" className="lastUpdateInfo" />
        </span>

        <UncontrolledTooltip
          className="tooltip-toolkit lastUpdateInfo"
          placement="bottom"
          target={tooltipId}
          modifiers={tooltipPreventOverflowModifiers}
        >
          {t('sprMigratedMsg')}
        </UncontrolledTooltip>
      </>
    );
  };

  const cells: Cell<OperationDocument>[] = [
    {
      name: 'documentName',
      column: 'document',
      render: (_, obj) => renderDocumentName(obj),
    },
    { name: 'createdBy', column: 'modifiedBy', value: (x) => x.createdBy },
    { name: 'createdOn', column: 'modifiedDate', value: (x) => dateStr(x.createdOn) },
    {
      render: (_, obj) =>
        obj.source === 'Legacy_Convergence' ? (
          <LinkButton
            text={t('download')}
            onClick={() => downloadEzshare(obj.ezShareDocumentCode)}
            icon="file-download"
          />
        ) : (
          <LinkButton text={t('download')} onClick={() => download(obj.id)} icon="file-download" />
        ),
    },
    {
      render: (x, obj) =>
        canEdit && obj.canDelete ? (
          <LinkButton onClick={() => showDeleteModal(obj)} icon="trash-alt" />
        ) : (
          <></>
        ),
    },
  ];

  const actions = [
    { text: t('cancel') },
    { text: t('keepBoth'), action: () => conflictUpload(false) },
    { text: t('replace'), action: () => conflictUpload(true) },
  ];

  return (
    <>
      <div className="details-bar">
        <div className="content">
          <h4>
            {data &&
              data.total > 0 &&
              t('showingDocuments', { from: data.from, to: data.to, total: data.total })}
          </h4>
        </div>
        <div className="mr-auto" />
        {canEdit && (
          <div className="operation-home-upload-documents">
            {errorUpload && <span className="form__form-group-error">{errorUpload}</span>}

            <FileUploadButton
              htmlId="btnUpload"
              text={t('uploadNew')}
              onClick={upload}
              onError={uploadFileError}
            />
          </div>
        )}
      </div>
      <SpinnerArea loading={loading}>
        {data && data.total > 0 && (
          <>
            <Table
              data={data.list}
              cells={cells}
              keyFunc={(x) => x.id}
              sortData={filter}
              changeSort={changeSort}
            />
            <TablePagination
              pageSize={pageSize}
              pageNumber={filter.pageNumber}
              total={data.total}
              onChangePage={changePage}
            />
          </>
        )}
      </SpinnerArea>
      <ConfirmModal
        title={t('deleteDocument')}
        buttonText={t('delete')}
        visible={docSelected != null}
        close={closeModal}
        action={() => deleteDoc()}
      >
        {t('deleteDocumentMsg', { file: docSelected && docSelected.documentName })}
      </ConfirmModal>
      <Modal actions={actions} visible={conflict} setVisible={setConflict}>
        <span>{t('nameConflictMsg1')}</span>
        <span className="bold">{formData && (formData.get('file') as File).name}</span>
        <span>{t('nameConflictMsg2')}</span>
      </Modal>
    </>
  );
};

export default Documents;
